import React, { useState } from 'react'
import axios from 'axios'
import { useDispatch } from 'react-redux'
import { Button, Modal, Spinner } from 'react-bootstrap'
import { toast } from 'react-toastify'
import { fetchAllPublicKeys, deletePublicKey } from '../integrationsSlice'
import { fetchAllSecrets } from '../../../features/aws/awsSecretsManagerSlice'
import { getAxiosDefaultConfig } from '../../../modules/utils'
import { shippoDisconnectUrl } from '../smUninstallEndpoints'
import { isShippoLegacyIntegrationKeyId } from '../integrationTags'
import type { ShippoDisconnectAccount } from '../shippoDisconnectResolve'

export type { ShippoDisconnectAccount }

type Props = {
  visible: boolean
  closeModal: () => void
  account: ShippoDisconnectAccount | null
  channelTitle: string
  ownerId: string | number | null | undefined
  retrieveAccounts?: () => Promise<void>
}

function disconnectPayload(account: ShippoDisconnectAccount | null): Record<string, string> {
  if (!account) return {}
  const legacy = isShippoLegacyIntegrationKeyId(account._keyId)
  const mid = String(account.merchant_id || '').trim()
  const ch = String(account.payhelm_store_channel || '').trim()
  if (legacy || (!mid && !ch)) return {}
  return { merchant_id: mid, channel: ch }
}

export default function ShippoDisconnectModal({
  visible,
  closeModal,
  account,
  channelTitle,
  ownerId,
  retrieveAccounts,
}: Props) {
  const dispatch = useDispatch()
  const [isProcessing, setIsProcessing] = useState(false)
  const [completed, setCompleted] = useState(false)
  const [showError, setShowError] = useState(false)

  const handleConfirm = async () => {
    if (!account) return
    setIsProcessing(true)
    setShowError(false)
    try {
      const data = disconnectPayload(account)
      await axios.delete(shippoDisconnectUrl, {
        ...getAxiosDefaultConfig(),
        data,
      })
      if (account._keyId) {
        try {
          await dispatch(deletePublicKey({ keyId: account._keyId })).unwrap()
        } catch {
          /* row may already be gone */
        }
      }
      if (ownerId != null && String(ownerId).trim() !== '') {
        await dispatch(fetchAllPublicKeys({ ownerId })).unwrap()
        await dispatch(fetchAllSecrets({ ownerId })).unwrap()
      }
      if (retrieveAccounts) {
        await retrieveAccounts()
      }
      setCompleted(true)
      toast.success(`${channelTitle} disconnected for this store`)
    } catch (err: any) {
      console.error('Error disconnecting Shippo:', err)
      setShowError(true)
      toast.error(err?.response?.data?.error || 'Failed to disconnect Shippo. Please try again.')
    } finally {
      setIsProcessing(false)
    }
  }

  const handleClose = () => {
    closeModal()
    setCompleted(false)
    setShowError(false)
  }

  return (
    <Modal show={visible} onHide={handleClose}>
      <Modal.Body>
        <div className="tw-p-6">
          <h2 className="tw-text-xl tw-mb-6">Disconnect {channelTitle}</h2>
          {showError && (
            <p className="tw-text-red-600 tw-mb-4">Error disconnecting. Please try again.</p>
          )}
          <p className="tw-mb-4">
            {completed
              ? 'This store’s Shippo connection has been removed.'
              : `Remove Shippo for this store? Label purchasing and webhooks for this connection will stop until you connect again.`}
          </p>
          <div className="tw-flex tw-justify-end tw-space-x-2">
            <Button variant="secondary" size="sm" onClick={handleClose}>
              {completed ? 'Done' : 'Cancel'}
            </Button>
            {!completed && (
              <Button variant="danger" size="sm" onClick={handleConfirm} disabled={isProcessing || !account}>
                {isProcessing ? (
                  <>
                    <Spinner animation="border" size="sm" className="tw-mr-2" />
                    Disconnecting...
                  </>
                ) : (
                  'Disconnect'
                )}
              </Button>
            )}
          </div>
        </div>
      </Modal.Body>
    </Modal>
  )
}
